import { hasGame, S, setGame } from '../core/game';
import { loadGame, saveGame } from '../core/save';
import { storage } from '../core/storage';
import type { GameState } from '../core/state';
import { toast } from '../core/effects';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { h } from './dom';
import { openPanel, refreshPanel } from './panel';

/** Save codes: base64 of the JSON of the game state, to back up a game or move it to another device. */
export function encodeSave(s: GameState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(s));
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Decode and sanity-check a code. Returns the JSON text, or null when it isn't a valid save. */
export function decodeSave(code: string): string | null {
  try {
    const bin = atob(code.replace(/\s+/g, ''));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const raw = JSON.parse(json) as Partial<GameState>;
    if (!raw || typeof raw !== 'object' || raw.version !== 3 || !Number.isFinite(raw.dayIndex) || !Number.isFinite(raw.time) || !raw.player) return null;
    return json;
  } catch {
    return null;
  }
}

/**
 * Import: write the save to storage, check that the game can load it, then reload.
 * The live state is swapped for the imported one with a frozen player, so the
 * page-hide autosave can't overwrite the import with the current position.
 */
export function importSave(code: string): boolean {
  const json = decodeSave(code);
  if (!json) return false;
  const prev = storage.get('save');
  storage.set('save', json);
  const loaded = loadGame();
  if (!loaded) {
    if (prev) storage.set('save', prev);
    else storage.remove('save');
    return false;
  }
  Object.freeze(loaded.player);
  setGame(loaded);
  location.reload();
  return true;
}

const ui = { pasted: '', bad: false, copied: false, confirm: false };

export function openSaveCode(fromStart = false): void {
  ui.pasted = '';
  ui.bad = false;
  ui.copied = false;
  ui.confirm = false;
  if (!fromStart && hasGame()) saveGame(S());
  openPanel('lifeSave', () => {
    const t = m().life.save;
    const json = storage.get('save');
    const code = !fromStart && hasGame() ? encodeSave(S()) : json ? encodeSave(JSON.parse(json) as GameState) : '';
    const out = h('textarea', { class: 'life-code', rows: 3, readonly: true }, code) as HTMLTextAreaElement;
    const inp = h('textarea', { class: 'life-code', rows: 3, placeholder: t.placeholder }) as HTMLTextAreaElement;
    inp.value = ui.pasted;
    inp.addEventListener('input', () => {
      ui.pasted = inp.value;
      ui.bad = false;
    });
    inp.addEventListener('keydown', (e) => e.stopPropagation());
    return {
      title: t.title,
      className: 'life-save',
      body: [
        code
          ? [
              h('p', { class: 'hint' }, t.exportHint),
              out,
              h(
                'button',
                {
                  class: 'btn small blue wide',
                  onclick: async () => {
                    try {
                      await navigator.clipboard.writeText(code);
                    } catch {
                      out.select();
                      document.execCommand?.('copy');
                    }
                    ui.copied = true;
                    sfx('ok');
                    toast(t.copied);
                    refreshPanel('lifeSave');
                  },
                },
                ui.copied ? `✅ ${t.copied}` : t.copy,
              ),
            ]
          : null,
        h('p', { class: 'hint' }, t.importHint),
        inp,
        ui.bad ? h('p', { class: 'hint bad' }, t.bad) : null,
        ui.confirm ? h('p', { class: 'hint big' }, t.confirm) : null,
        h(
          'button',
          {
            class: `btn small ${ui.confirm ? 'red' : 'gold'} wide`,
            onclick: () => {
              if (!decodeSave(ui.pasted)) {
                ui.bad = true;
                sfx('bad');
                return refreshPanel('lifeSave');
              }
              if (!ui.confirm && (storage.get('save') || hasGame())) {
                ui.confirm = true;
                return refreshPanel('lifeSave');
              }
              if (!importSave(ui.pasted)) {
                ui.bad = true;
                sfx('bad');
                refreshPanel('lifeSave');
              }
            },
          },
          ui.confirm ? `⚠️ ${t.load}` : t.load,
        ),
      ],
    };
  });
}
